import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { leaveApi } from "../../services/leaveApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function LeaveEmployeeDetailPage() {
  const { employeeId } = useParams();
  const { t } = useLocalization();
  const { canViewLeave, canManageLeave } = usePermissions();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [grantForm, setGrantForm] = useState({ policy_id: "", days_granted: "", grant_date: "", expires_at: "" });
  const [eligibility, setEligibility] = useState(null);
  const [adjustment, setAdjustment] = useState({ days: "", reason: "" });
  const [forceGrantOpen, setForceGrantOpen] = useState(false);

  const load = useCallback(() => {
    leaveApi.employeeBalance(employeeId).then((r) => setBalance(r.data)).catch(() => {});
    leaveApi.employeeLedger(employeeId).then((r) => setLedger(r.data)).catch(() => {});
    leaveApi.listPolicies().then((r) => setPolicies(r.data)).catch(() => {});
  }, [employeeId]);

  useEffect(() => { if (canViewLeave) load(); }, [canViewLeave, load]);

  if (!canViewLeave) return <p className="text-sm text-gray-500" data-testid="leave-employee-detail-access-denied">{t("common.accessDenied")}</p>;

  const policyTitle = (id) => policies.find((p) => p.id === id)?.title_en || id;

  const checkEligibility = async () => {
    if (!grantForm.policy_id) { toast.error(t("mgmtLeaveDetail.choosePolicyFirst")); return; }
    try { const { data } = await leaveApi.checkEligibility(employeeId, grantForm.policy_id); setEligibility(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const grant = async (forceReason) => {
    if (!grantForm.policy_id || !grantForm.days_granted || !grantForm.grant_date) { toast.error(t("mgmtLeaveDetail.policyDaysGrantDateRequired")); return; }
    await leaveApi.grantLeave({ employee_id: employeeId, policy_id: grantForm.policy_id, days_granted: Number(grantForm.days_granted), grant_date: grantForm.grant_date, expires_at: grantForm.expires_at || undefined, force: !!forceReason, force_reason: forceReason });
    toast.success(t("mgmtLeaveDetail.leaveGranted"));
    setGrantForm({ policy_id: "", days_granted: "", grant_date: "", expires_at: "" });
    setEligibility(null);
    load();
  };

  const submitAdjustment = async () => {
    if (!adjustment.days || !adjustment.reason) { toast.error(t("mgmtLeaveDetail.daysAndReasonRequired")); return; }
    try {
      await leaveApi.manualAdjustment({ employee_id: employeeId, days: Number(adjustment.days), reason: adjustment.reason });
      toast.success(t("mgmtLeaveDetail.adjustmentPosted"));
      setAdjustment({ days: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="leave-employee-detail-page">
      <PageHelpBanner pageKey="leave-employee-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtLeaveDetail.help")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLeaveDetail.title")}</h2>
        {balance && <p className="text-sm text-gray-500">{t("mgmtLeaveDetail.currentBalance")}: <span className="font-bold text-gray-900" data-testid="leave-current-balance">{balance.current_balance} {t("mgmtLeaveDetail.days")}</span></p>}
      </div>

      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[[t("mgmtLeaveDetail.granted"), balance.granted_total], [t("mgmtLeaveDetail.used"), balance.used_total], [t("mgmtLeaveDetail.futureReserved"), balance.future_reserved_total], [t("mgmtLeaveDetail.expired"), balance.expired_total]].map(([label, val]) => (
            <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-2xl font-bold text-gray-900">{val}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {canManageLeave && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="grant-leave-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtLeaveDetail.grantLeave")}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select value={grantForm.policy_id} onValueChange={(v) => { setGrantForm({ ...grantForm, policy_id: v }); setEligibility(null); }}>
              <SelectTrigger data-testid="grant-policy-select"><SelectValue placeholder={t("mgmtLeaveDetail.policyPlaceholder")} /></SelectTrigger>
              <SelectContent>{policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.title_en} ({p.status})</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="grant-days-input" type="number" value={grantForm.days_granted} onChange={(e) => setGrantForm({ ...grantForm, days_granted: e.target.value })} placeholder={t("mgmtLeaveDetail.days")} />
            <Input data-testid="grant-date-input" type="date" value={grantForm.grant_date} onChange={(e) => setGrantForm({ ...grantForm, grant_date: e.target.value })} />
            <div className="space-y-1">
              <label className="text-[11px] text-gray-500 block">{t("mgmtLeaveDetail.expiresOptional")}</label>
              <Input data-testid="grant-expires-at-input" type="date" value={grantForm.expires_at} onChange={(e) => setGrantForm({ ...grantForm, expires_at: e.target.value })} />
            </div>
            <Button variant="outline" data-testid="check-eligibility-btn" onClick={checkEligibility}>{t("mgmtLeaveDetail.checkEligibility")}</Button>
          </div>
          {eligibility && (
            <div className="flex items-center gap-2" data-testid="eligibility-result">
              <StatusBadge status={eligibility.status} />
              {eligibility.blockers.length > 0 && <span className="text-xs text-red-600">{eligibility.blockers.join(", ")}</span>}
            </div>
          )}
          <div className="flex gap-2">
            <Button data-testid="grant-leave-btn" disabled={!eligibility || eligibility.status !== "ELIGIBLE"} onClick={() => grant(undefined).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail)))}>{t("mgmtLeaveDetail.grant")}</Button>
            {eligibility && eligibility.status !== "ELIGIBLE" && (
              <Button variant="outline" data-testid="force-grant-leave-btn" onClick={() => setForceGrantOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50">{t("mgmtLeaveDetail.forceGrantWithReason")}</Button>
            )}
          </div>
        </div>
      )}

      {canManageLeave && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="manual-adjustment-panel">
          <Input data-testid="adjustment-days-input" type="number" value={adjustment.days} onChange={(e) => setAdjustment({ ...adjustment, days: e.target.value })} placeholder={t("mgmtLeaveDetail.daysPlusMinus")} />
          <Input data-testid="adjustment-reason-input" value={adjustment.reason} onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })} placeholder={t("mgmtLeaveDetail.reasonRequired")} className="col-span-2" />
          <Button variant="outline" data-testid="submit-adjustment-btn" onClick={submitAdjustment}>{t("mgmtLeaveDetail.postManualAdjustment")}</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-gray-100">{t("mgmtLeaveDetail.immutableLedger")}</p>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("common.date")}</th><th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colType")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.days")}</th><th className="px-4 py-3 font-medium">{t("mgmtRecruitment.reason")}</th></tr></thead>
          <tbody>
            {ledger.map((tx) => (
              <tr key={tx.id} className="border-t border-gray-100" data-testid={`ledger-row-${tx.id}`}>
                <td className="px-4 py-2 text-gray-600">{tx.effective_date}</td>
                <td className="px-4 py-2"><StatusBadge status={tx.type} /></td>
                <td className={`px-4 py-2 font-semibold ${tx.days >= 0 ? "text-emerald-600" : "text-red-600"}`}>{tx.days > 0 ? "+" : ""}{tx.days}</td>
                <td className="px-4 py-2 text-gray-600">{tx.reason || "-"}</td>
              </tr>
            ))}
            {ledger.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-ledger-rows">{t("mgmtLeaveDetail.noTransactions")}</td></tr>}
          </tbody>
        </table>
      </div>

      {eligibility && (
        <ReasonDialog
          open={forceGrantOpen} onOpenChange={setForceGrantOpen} title={t("mgmtLeaveDetail.forceGrantLeaveTitle")}
          description={t("mgmtLeaveDetail.forceGrantDescription")}
          contextItems={[
            { label: t("mgmtLeaveDetail.policy"), value: policyTitle(grantForm.policy_id) },
            { label: t("mgmtLeaveDetail.days"), value: grantForm.days_granted },
            { label: t("mgmtLeaveDetail.grantDate"), value: grantForm.grant_date },
            { label: t("mgmtLeaveDetail.expires"), value: grantForm.expires_at || t("mgmtLeaveDetail.never") },
            { label: t("mgmtLeaveDetail.eligibilityStatus"), value: eligibility.status },
            { label: t("mgmtLeaveDetail.blockers"), value: eligibility.blockers.join(", ") || "-" },
          ]}
          reasonLabel={t("mgmtLeaveDetail.reasonToOverride")} danger confirmLabel={t("mgmtLeaveDetail.forceGrant")}
          onConfirm={grant} testId="force-grant-dialog"
        />
      )}
    </div>
  );
}

